import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import QuickRechargeButton from "@/components/QuickRechargeButton";
import { api, formatEUR, formatApiError, formatNum } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    ArrowLeft, Bank, CheckCircle, Warning, XCircle, Sparkle, ArrowRight, ArrowsLeftRight, Lightbulb,
} from "@phosphor-icons/react";

const BALANCE_FIELDS = [
    { k: "activos_no_corrientes", label: "Activo no corriente", side: "activo", hint: "Inmovilizado material, intangible, inversiones LP" },
    { k: "activos_corrientes", label: "Activo corriente", side: "activo", hint: "Existencias, deudores, tesorería" },
    { k: "existencias", label: "— de los cuales: Existencias", side: "activo_sub", hint: "Mercaderías, productos terminados, obra en curso" },
    { k: "patrimonio_neto", label: "Patrimonio neto", side: "pasivo", hint: "Capital + reservas + resultado" },
    { k: "pasivos_no_corrientes", label: "Pasivo no corriente", side: "pasivo", hint: "Deudas > 1 año" },
    { k: "pasivos_corrientes", label: "Pasivo corriente", side: "pasivo", hint: "Deudas ≤ 1 año" },
];

const EXTRA_FIELDS = [
    { k: "obra_en_curso", label: "Obra en curso", group: "operativo", hint: "Stock de obra o proyectos pendientes de certificar" },
    { k: "aprovisionamientos", label: "Aprovisionamientos / consumos", group: "operativo", hint: "Compras + variación existencias + subcontratas" },
    { k: "gastos_personal", label: "Gastos de personal", group: "operativo" },
    { k: "amortizaciones", label: "Amortizaciones del ejercicio", group: "operativo" },
    { k: "gastos_financieros", label: "Gastos financieros", group: "operativo", hint: "Intereses pagados" },
    { k: "efectivo", label: "Efectivo y bancos", group: "tesoreria", hint: "Caja + cuentas corrientes" },
    { k: "deudores_comerciales", label: "Deudores comerciales", group: "tesoreria" },
    { k: "deudores_mas_180d", label: "Deudores con > 180 días", group: "tesoreria", hint: "Saldos antiguos sin cobrar" },
    { k: "prestamos_socios", label: "Préstamos de socios", group: "estructura", hint: "Cuentas con socios — pueden reclasificarse como cuasi-equity" },
    { k: "reservas", label: "Reservas (legales + voluntarias)", group: "estructura" },
    { k: "resultado_ejercicio_anterior", label: "Resultado ejercicio anterior pendiente de aplicar", group: "estructura" },
];

const SECTORS = [
    { v: "general", l: "General / Industria" },
    { v: "construccion", l: "Construcción / Obras" },
    { v: "servicios", l: "Servicios profesionales" },
    { v: "comercio", l: "Comercio / Distribución" },
    { v: "hosteleria", l: "Hostelería / Restauración" },
];

function safeDiv(a, b) {
    if (!b || b === 0) return null;
    return a / b;
}

function ratioLevel(name, value) {
    if (value === null || value === undefined || Number.isNaN(value)) return "n/a";
    switch (name) {
        case "ratio_corriente":
            return value >= 1.2 ? "verde" : value >= 1.0 ? "ambar" : "rojo";
        case "autonomia":
            return value >= 0.30 ? "verde" : value >= 0.20 ? "ambar" : "rojo";
        case "apalancamiento":
            return value <= 2.5 ? "verde" : value <= 4.0 ? "ambar" : "rojo";
        default:
            return "n/a";
    }
}

function computeRatios(balance, extra = {}) {
    const ac = Number(balance.activos_corrientes) || 0;
    const anc = Number(balance.activos_no_corrientes) || 0;
    const pc = Number(balance.pasivos_corrientes) || 0;
    const pnc = Number(balance.pasivos_no_corrientes) || 0;
    const pn = Number(balance.patrimonio_neto) || 0;
    const ing = Number(balance.ingresos) || 0;
    const gas = Number(balance.gastos) || 0;
    const total_activo = ac + anc;
    const total_pasivo = pc + pnc;

    // Resultado: si viene informado se respeta, si no, ingresos − gastos
    const resultado =
        balance.resultado !== undefined && balance.resultado !== "" && balance.resultado !== null
            ? Number(balance.resultado) || 0
            : ing - gas;

    // Extras (para deuda/EBITDA y DSCR)
    const amortizaciones = Number(extra.amortizaciones) || 0;
    const gastosFin = Number(extra.gastos_financieros) || 0;
    const ebit = resultado + gastosFin;
    const ebitda = ebit + amortizaciones;
    const deudaEbitda = ebitda > 0 ? safeDiv(total_pasivo, ebitda) : null;
    const dscrDen = gastosFin + pc * 0.3;
    const dscr = dscrDen > 0 ? safeDiv(ebitda, dscrDen) : null;

    const corriente = safeDiv(ac, pc);
    const autonomia = safeDiv(pn, total_activo);
    const apalanc = safeDiv(total_pasivo, pn);

    return {
        ratio_corriente: { value: corriente, label: "Ratio corriente", optimal: "≥ 1.2", level: ratioLevel("ratio_corriente", corriente), kind: "x" },
        autonomia: { value: autonomia, label: "Autonomía financiera", optimal: "≥ 30%", level: ratioLevel("autonomia", autonomia), kind: "pct" },
        apalancamiento: { value: apalanc, label: "Apalancamiento", optimal: "≤ 2.5", level: ratioLevel("apalancamiento", apalanc), kind: "x" },
        deuda_ebitda: {
            value: deudaEbitda,
            label: "Deuda neta / EBITDA",
            optimal: "≤ 4.0",
            level:
                deudaEbitda === null
                    ? "neutral"
                    : deudaEbitda <= 4.0
                    ? "verde"
                    : deudaEbitda <= 6.0
                    ? "ambar"
                    : "rojo",
            kind: "x",
            requires: !ebitda || ebitda <= 0 ? "Introduce amortizaciones y gastos financieros para calcular" : null,
        },
        dscr: {
            value: dscr,
            label: "Cobertura servicio deuda",
            optimal: "≥ 1.2",
            level:
                dscr === null
                    ? "neutral"
                    : dscr >= 1.2
                    ? "verde"
                    : dscr >= 1.0
                    ? "ambar"
                    : "rojo",
            kind: "x",
            requires: dscrDen <= 0 ? "Introduce gastos financieros para calcular" : null,
        },
    };
}

export default function BankAudit() {
    const { id } = useParams();
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [errorStatus, setErrorStatus] = useState(null);
    const [paywallModal, setPaywallModal] = useState(null); // {detail} para bloqueo de modificación extra
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    // Balance corregido (lo que el usuario va editando)
    const [corrected, setCorrected] = useState({});
    // Datos adicionales para las 7 reglas
    const [extra, setExtra] = useState({
        obra_en_curso: "",
        aprovisionamientos: "",
        gastos_personal: "",
        amortizaciones: "",
        gastos_financieros: "",
        efectivo: "",
        deudores_comerciales: "",
        deudores_mas_180d: "",
        prestamos_socios: "",
        reservas: "",
        resultado_ejercicio_anterior: "",
        sector: "general",
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/analyses/${id}`);
                setAnalysis(data);
                // Pre-cargar el balance original como corregido (editable)
                const b = data.balance || {};
                setCorrected({
                    activos_no_corrientes: b.activos_no_corrientes ?? 0,
                    activos_corrientes: b.activos_corrientes ?? 0,
                    existencias: b.existencias ?? 0,
                    patrimonio_neto: b.patrimonio_neto ?? 0,
                    pasivos_no_corrientes: b.pasivos_no_corrientes ?? 0,
                    pasivos_corrientes: b.pasivos_corrientes ?? 0,
                    ingresos: b.ingresos ?? 0,
                    gastos: b.gastos ?? 0,
                    resultado: b.resultado ?? 0,
                });
                // Pre-cargar existencias en obra en curso como sugerencia
                if (b.existencias) {
                    setExtra((e) => ({ ...e, obra_en_curso: String(b.existencias) }));
                }
            } catch (e) {
                setError(formatApiError(e, "No se pudo cargar el análisis"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const originalBalance = analysis?.balance || {};
    const totalsOriginal = useMemo(() => {
        const a = (originalBalance.activos_corrientes || 0) + (originalBalance.activos_no_corrientes || 0);
        const p = (originalBalance.patrimonio_neto || 0) + (originalBalance.pasivos_corrientes || 0) + (originalBalance.pasivos_no_corrientes || 0);
        return { activo: a, pasivo: p, dif: a - p, cuadra: Math.abs(a - p) < Math.max(1, a * 0.01) };
    }, [originalBalance]);

    const totalsCorrected = useMemo(() => {
        const a = (Number(corrected.activos_corrientes) || 0) + (Number(corrected.activos_no_corrientes) || 0);
        const p = (Number(corrected.patrimonio_neto) || 0) + (Number(corrected.pasivos_corrientes) || 0) + (Number(corrected.pasivos_no_corrientes) || 0);
        return { activo: a, pasivo: p, dif: a - p, cuadra: Math.abs(a - p) < Math.max(1, a * 0.01) };
    }, [corrected]);

    const ratiosBefore = useMemo(() => computeRatios(originalBalance, extra), [originalBalance, extra]);
    const ratiosAfter = useMemo(() => computeRatios(corrected, extra), [corrected, extra]);

    function setBalanceField(k, v) {
        setCorrected((c) => ({ ...c, [k]: v === "" ? "" : Number(v) }));
    }

    function resetToOriginal() {
        const b = analysis?.balance || {};
        setCorrected({
            activos_no_corrientes: b.activos_no_corrientes ?? 0,
            activos_corrientes: b.activos_corrientes ?? 0,
            existencias: b.existencias ?? 0,
            patrimonio_neto: b.patrimonio_neto ?? 0,
            pasivos_no_corrientes: b.pasivos_no_corrientes ?? 0,
            pasivos_corrientes: b.pasivos_corrientes ?? 0,
            ingresos: b.ingresos ?? 0,
            gastos: b.gastos ?? 0,
            resultado: b.resultado ?? 0,
        });
    }

    function setExtraField(k, v) {
        setExtra((e) => ({ ...e, [k]: v }));
    }

    async function generateAudit() {
        setError(""); setSubmitting(true);
        try {
            const payload = {
                obra_en_curso: Number(extra.obra_en_curso) || 0,
                aprovisionamientos: Number(extra.aprovisionamientos) || 0,
                gastos_personal: Number(extra.gastos_personal) || 0,
                amortizaciones: Number(extra.amortizaciones) || 0,
                gastos_financieros: Number(extra.gastos_financieros) || 0,
                efectivo: Number(extra.efectivo) || 0,
                deudores_comerciales: Number(extra.deudores_comerciales) || 0,
                deudores_mas_180d: Number(extra.deudores_mas_180d) || 0,
                prestamos_socios: Number(extra.prestamos_socios) || 0,
                reservas: Number(extra.reservas) || 0,
                resultado_ejercicio_anterior: Number(extra.resultado_ejercicio_anterior) || 0,
                sector: extra.sector || "general",
                corrected_balance: {
                    activos_corrientes: Number(corrected.activos_corrientes) || 0,
                    activos_no_corrientes: Number(corrected.activos_no_corrientes) || 0,
                    existencias: Number(corrected.existencias) || 0,
                    pasivos_corrientes: Number(corrected.pasivos_corrientes) || 0,
                    pasivos_no_corrientes: Number(corrected.pasivos_no_corrientes) || 0,
                    patrimonio_neto: Number(corrected.patrimonio_neto) || 0,
                    ingresos: Number(corrected.ingresos) || 0,
                    gastos: Number(corrected.gastos) || 0,
                    resultado: Number(corrected.resultado) || 0,
                },
            };
            const { data } = await api.post(`/analyses/${id}/bank-audit`, payload);
            setResult(data);
            refresh();
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            // Detectar paywall de modificación extra (2ª+): backend envía header X-Requires-Modification
            const status = e?.response?.status;
            const requiresMod = e?.response?.headers?.["x-requires-modification"] === "1";
            const detail = e?.response?.data?.detail || "";
            if (status === 402 && (requiresMod || detail.includes("modificación"))) {
                setPaywallModal({ detail });
            } else {
                setError(formatApiError(e, "No se pudo generar el dictamen"));
                setErrorStatus(status || null);
            }
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <span className="elegant-loader"><span /><span /><span /></span>
            </div>
        );
    }
    if (!analysis) {
        return (
            <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <p className="text-[var(--gf-danger)] mb-4">{error || "Análisis no encontrado"}</p>
                        <Link to="/dashboard" className="btn-outline">Volver al panel</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-[var(--gf-cream)]/40">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-10">
                {/* Header */}
                <div className="mb-8">
                    <Link to={`/analisis/${id}`} className="text-sm text-[var(--gf-text-secondary)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 mb-3">
                        <ArrowLeft size={14} /> Volver al informe
                    </Link>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div>
                            <div className="micro-label mb-2">— Dictamen pre-bancario · Más de 30 años de experiencia</div>
                            <h1 className="serif text-4xl md:text-5xl leading-tight">
                                Auditoría <span className="italic text-[var(--gf-gold)]">bancaria</span>
                            </h1>
                            <p className="text-sm text-[var(--gf-text-secondary)] mt-2">
                                {analysis.company_name} · Ejercicio <span className="mono">{analysis.period}</span>
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[var(--gf-text-muted)]">
                            <Bank size={18} weight="light" className="text-[var(--gf-navy)]" />
                            <span>Incluido en Balance Master · 5 € por modificación extra</span>
                        </div>
                    </div>
                </div>

                {result ? (
                    <ResultView result={result} onReset={() => setResult(null)} analysisId={id} />
                ) : (
                    <>
                        {/* Intro */}
                        <div className="bg-[var(--gf-navy)] text-white p-6 mb-8" data-testid="audit-intro">
                            <div className="flex gap-4">
                                <Sparkle size={24} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-1" />
                                <div>
                                    <h2 className="serif text-xl mb-2">¿Cómo funciona la auditoría?</h2>
                                    <p className="text-sm text-white/80 leading-relaxed">
                                        Te mostramos las <strong className="text-[var(--gf-gold)]">magnitudes de tu balance ya extraídas</strong>. Modifica
                                        únicamente las partidas que sepas que pueden estar mal clasificadas (por ejemplo, préstamos de socios
                                        reclasificados, obra en curso facturada, provisiones por morosos…). El sistema validará que el balance
                                        <strong> siga cuadrando</strong>. A la derecha verás cómo cambian tus ratios en tiempo real.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* COL 1-2: Balance editable */}
                            <div className="lg:col-span-2 space-y-6">
                                <BalanceEditor
                                    corrected={corrected}
                                    original={originalBalance}
                                    onChange={setBalanceField}
                                    totalsCorrected={totalsCorrected}
                                    totalsOriginal={totalsOriginal}
                                    onReset={resetToOriginal}
                                />

                                <ExtraDataForm
                                    extra={extra}
                                    onChange={setExtraField}
                                    balance={corrected}
                                    totals={totalsCorrected}
                                />
                            </div>

                            {/* COL 3: Ratios live preview */}
                            <div className="space-y-4">
                                <div className="gf-card sticky top-24">
                                    <div className="flex items-center gap-2 mb-1">
                                        <ArrowsLeftRight size={18} weight="light" className="text-[var(--gf-gold)]" />
                                        <span className="micro-label">— Antes vs Después</span>
                                    </div>
                                    <h3 className="serif text-xl mb-4">Ratios en tiempo real</h3>
                                    <div className="space-y-3" data-testid="ratios-live">
                                        {Object.entries(ratiosAfter).map(([key, r]) => (
                                            <RatioCompare key={key} before={ratiosBefore[key]} after={r} />
                                        ))}
                                    </div>

                                    {!totalsCorrected.cuadra && totalsCorrected.activo > 0 && (
                                        <div className="mt-4 text-xs text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50" data-testid="balance-no-cuadra">
                                            ⚠️ Balance corregido no cuadra. Diferencia: {formatEUR(totalsCorrected.dif)}
                                        </div>
                                    )}

                                    {error && (
                                        <div className="mt-4 text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50" data-testid="audit-error">
                                            {error}
                                            {errorStatus === 402 && (
                                                <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="audit-recharge-block">
                                                    <QuickRechargeButton amount={44} nextPath={`/analisis/${id}/auditoria-bancaria`} className="btn-gold text-xs" testId="quick-recharge-audit" onError={(m) => setError(m)} />
                                                    <Link to="/pagos" className="text-xs text-[var(--gf-navy)] underline underline-offset-2" data-testid="audit-other-recharges">Otras recargas</Link>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        onClick={generateAudit}
                                        disabled={submitting || !totalsCorrected.cuadra}
                                        className="btn-primary w-full justify-center mt-5"
                                        data-testid="generate-audit-btn"
                                    >
                                        {submitting ? (
                                            <><span className="elegant-loader"><span /><span /><span /></span> &nbsp; Generando…</>
                                        ) : (
                                            <>Generar dictamen pre-bancario <ArrowRight size={16} /></>
                                        )}
                                    </button>
                                    <p className="text-[10px] text-center text-[var(--gf-text-muted)] mt-3">
                                        Aplica las 7 reglas profesionales · Más de 30 años de experiencia
                                    </p>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </main>
            {paywallModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--gf-navy)]/70 backdrop-blur-sm px-4"
                    data-testid="modif-paywall-modal"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className="bg-white max-w-md w-full shadow-2xl border-t-4 border-t-[var(--gf-gold)]"
                    >
                        <div className="p-6 md:p-8">
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkle size={18} weight="fill" className="text-[var(--gf-gold)]" />
                                <span className="micro-label">— Modificación extra</span>
                            </div>
                            <h3 className="serif text-2xl text-[var(--gf-navy)] mb-3">Comprar modificación</h3>
                            <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-4">
                                Ya has usado tu 1ª modificación gratuita para este balance. La 2ª modificación y siguientes tienen un coste de <strong className="text-[var(--gf-navy)]">5 € + IVA</strong> (6,05 € IVA incluido) por rectificación.
                            </p>
                            <div className="bg-[var(--gf-subtle)] p-3 mb-5 text-xs text-[var(--gf-text-secondary)] leading-relaxed">
                                💡 <strong>Tus cambios están guardados temporalmente.</strong> Si cancelas ahora no perderás las cifras rectificadas —quedarán en pantalla para que puedas revisarlas o pagar la modificación después.
                            </div>
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={() => setPaywallModal(null)}
                                    className="btn-outline text-sm justify-center"
                                    data-testid="modif-paywall-cancel"
                                >
                                    Salir sin perder datos
                                </button>
                                <QuickRechargeButton
                                    amount={5}
                                    nextPath={`/analisis/${id}/auditoria-bancaria`}
                                    label="Recargar 5 créditos y modificar · 6,05 € IVA incl."
                                    className="btn-gold justify-center"
                                    testId="modif-paywall-buy"
                                    onError={(m) => { setError(m); setPaywallModal(null); }}
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
            <Footer />
            <WhatsAppFab />
        </div>
    );
}

/* ============================================================ */
/*  BalanceEditor: tabla editable Antes vs Después               */
/* ============================================================ */
function BalanceEditor({ corrected, original, onChange, totalsCorrected, totalsOriginal, onReset }) {
    function isChanged(k) {
        const o = Number(original[k]) || 0;
        const c = Number(corrected[k]) || 0;
        return Math.abs(o - c) > 0.5;
    }

    return (
        <div className="gf-card-ficha" data-testid="balance-editor">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <span className="micro-label">— Balance editable</span>
                    <h3 className="serif text-2xl mt-1">Tus magnitudes</h3>
                </div>
                <button onClick={onReset} className="text-xs text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)] underline underline-offset-4" data-testid="reset-balance-btn">
                    Restablecer originales
                </button>
            </div>

            <div className="text-xs text-[var(--gf-text-secondary)] mb-4">
                Solo edita las casillas que necesiten corrección. El resto se mantiene como estaba.
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {/* Cabeceras */}
                <div className="text-center pb-2 border-b border-[var(--gf-gold)]">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--gf-gold)] font-semibold">Activo</span>
                </div>
                <div className="text-center pb-2 border-b border-[var(--gf-navy)]">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--gf-navy)] font-semibold">Patrimonio + Pasivo</span>
                </div>

                {/* Activo (izq) */}
                <div className="space-y-3">
                    {BALANCE_FIELDS.filter((f) => f.side === "activo" || f.side === "activo_sub").map((f) => (
                        <EditableMagnitude
                            key={f.k}
                            label={f.label}
                            hint={f.hint}
                            originalValue={original[f.k] ?? 0}
                            value={corrected[f.k] ?? ""}
                            onChange={(v) => onChange(f.k, v)}
                            changed={isChanged(f.k)}
                            indented={f.side === "activo_sub"}
                            testId={`balance-${f.k}`}
                        />
                    ))}
                </div>

                {/* Pasivo + PN (der) */}
                <div className="space-y-3">
                    {BALANCE_FIELDS.filter((f) => f.side === "pasivo").map((f) => (
                        <EditableMagnitude
                            key={f.k}
                            label={f.label}
                            hint={f.hint}
                            originalValue={original[f.k] ?? 0}
                            value={corrected[f.k] ?? ""}
                            onChange={(v) => onChange(f.k, v)}
                            changed={isChanged(f.k)}
                            testId={`balance-${f.k}`}
                        />
                    ))}
                </div>
            </div>

            {/* Totales */}
            <div className="grid grid-cols-2 gap-x-6 mt-5 pt-4 border-t border-[var(--gf-border)]">
                <TotalBox
                    label="Total Activo"
                    valueOriginal={totalsOriginal.activo}
                    valueCorrected={totalsCorrected.activo}
                    accent="gold"
                />
                <TotalBox
                    label="Total PN + Pasivo"
                    valueOriginal={totalsOriginal.pasivo}
                    valueCorrected={totalsCorrected.pasivo}
                    accent="navy"
                />
            </div>

            <div className="mt-3">
                {totalsCorrected.cuadra ? (
                    <div className="text-xs text-[var(--gf-success)] border-l-2 border-[var(--gf-success)] pl-3 py-2 bg-green-50/60 flex items-center gap-2" data-testid="balance-cuadra">
                        <CheckCircle size={16} weight="fill" /> El balance corregido cuadra correctamente.
                    </div>
                ) : (
                    <div className="text-xs text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50">
                        ⚠️ El balance no cuadra. Diferencia: {formatEUR(totalsCorrected.dif)}. Ajusta hasta que Activo = PN + Pasivo.
                    </div>
                )}
            </div>
        </div>
    );
}

function EditableMagnitude({ label, hint, originalValue, value, onChange, changed, indented, testId }) {
    return (
        <div className={indented ? "pl-4 border-l border-[var(--gf-border)]" : ""}>
            <div className="flex items-baseline justify-between mb-1">
                <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] leading-tight" title={hint}>
                    {label}
                </label>
                {changed && (
                    <span className="text-[9px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold">Modificado</span>
                )}
            </div>
            <div className="relative">
                <input
                    type="number"
                    step="0.01"
                    className={`gf-input mono pr-8 text-sm ${changed ? "border-[var(--gf-gold)] bg-[var(--gf-cream)]" : ""}`}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="0"
                    data-testid={testId}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--gf-text-muted)] pointer-events-none">€</span>
            </div>
            <div className="text-[10px] text-[var(--gf-text-muted)] mt-1 mono">
                Original: {formatEUR(originalValue || 0)}
            </div>
        </div>
    );
}

function TotalBox({ label, valueOriginal, valueCorrected, accent }) {
    const colorClass = accent === "gold"
        ? "border-l-[var(--gf-gold)] text-[var(--gf-gold)]"
        : "border-l-[var(--gf-navy)] text-[var(--gf-navy)]";
    const changed = Math.abs((valueOriginal || 0) - (valueCorrected || 0)) > 0.5;
    return (
        <div className={`border-l-2 ${colorClass} bg-[var(--gf-cream)] px-3 py-2`}>
            <div className="text-[10px] tracking-widest uppercase text-[var(--gf-text-secondary)]">{label}</div>
            <div className="mono font-semibold text-base mt-0.5">{formatEUR(valueCorrected || 0)}</div>
            {changed && (
                <div className="text-[10px] mono text-[var(--gf-text-muted)] line-through">
                    {formatEUR(valueOriginal || 0)}
                </div>
            )}
        </div>
    );
}

/* ============================================================ */
/*  ExtraDataForm                                                */
/* ============================================================ */
function ExtraDataForm({ extra, onChange, balance = {}, totals = {} }) {
    const groups = {
        operativo: { label: "Datos operativos (Cuenta P&G)", fields: EXTRA_FIELDS.filter((f) => f.group === "operativo") },
        tesoreria: { label: "Tesorería y deudores", fields: EXTRA_FIELDS.filter((f) => f.group === "tesoreria") },
        estructura: { label: "Estructura patrimonial", fields: EXTRA_FIELDS.filter((f) => f.group === "estructura") },
    };
    // Chequeos de coherencia con el balance procesado
    const bal = {
        ac: Number(balance.activos_corrientes) || 0,
        anc: Number(balance.activos_no_corrientes) || 0,
        existencias: Number(balance.existencias) || 0,
        pn: Number(balance.patrimonio_neto) || 0,
        pnc: Number(balance.pasivos_no_corrientes) || 0,
        pc: Number(balance.pasivos_corrientes) || 0,
        ingresos: Number(balance.ingresos) || 0,
        gastos: Number(balance.gastos) || 0,
    };
    const rdo = bal.ingresos - bal.gastos;
    const gastosOperativos =
        (Number(extra.aprovisionamientos) || 0) +
        (Number(extra.gastos_personal) || 0) +
        (Number(extra.amortizaciones) || 0) +
        (Number(extra.gastos_financieros) || 0);
    const gastosDiff = bal.gastos - gastosOperativos;
    const gastosOk = bal.gastos === 0 || Math.abs(gastosDiff) <= Math.max(1, bal.gastos * 0.05);

    const efectivo = Number(extra.efectivo) || 0;
    const deudores = Number(extra.deudores_comerciales) || 0;
    const acDesglose = efectivo + deudores + bal.existencias;
    const acDiff = bal.ac - acDesglose;
    const acOk = bal.ac === 0 || acDesglose === 0 || Math.abs(acDiff) <= Math.max(1, bal.ac * 0.1);

    const reservas = Number(extra.reservas) || 0;
    const rdoAnt = Number(extra.resultado_ejercicio_anterior) || 0;
    const pnDesglose = reservas + rdoAnt + rdo;  // aprox: reservas + rdo anterior + rdo ejercicio
    const pnOk = bal.pn === 0 || pnDesglose === 0 || Math.abs(bal.pn - pnDesglose) <= Math.max(1, bal.pn * 0.15);

    return (
        <div className="gf-card-ficha" data-testid="extra-data-form">
            <div className="mb-4">
                <span className="micro-label">— Datos complementarios</span>
                <h3 className="serif text-2xl mt-1">Para aplicar las 7 reglas</h3>
                <p className="text-sm text-[var(--gf-text-secondary)] mt-1">
                    Cuanto más completes, más profundo será el dictamen. Lo que no sepas, déjalo en 0.
                </p>
            </div>

            {/* PANEL DE REFERENCIA: Balance procesado */}
            <div className="mb-6 border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-subtle)] p-4" data-testid="balance-reference-panel">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] tracking-widest uppercase font-semibold text-[var(--gf-gold)]">— Balance procesado · referencia</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                    <RefRow label="Activo no corriente" value={bal.anc} />
                    <RefRow label="Activo corriente" value={bal.ac} />
                    <RefRow label="— Existencias" value={bal.existencias} sub />
                    <RefRow label="Patrimonio Neto" value={bal.pn} highlight />
                    <RefRow label="Pasivo no corriente" value={bal.pnc} />
                    <RefRow label="Pasivo corriente" value={bal.pc} />
                    <RefRow label="Ingresos" value={bal.ingresos} />
                    <RefRow label="Gastos totales" value={bal.gastos} />
                    <RefRow label="Resultado (ing−gas)" value={rdo} highlight />
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--gf-border)] text-[10px] text-[var(--gf-text-muted)] leading-relaxed">
                    Cotéjalo con los saldos de los grupos contables al rellenar los datos: los desgloses deben aproximarse a estos totales.
                </div>
            </div>

            {/* Sector */}
            <div className="mb-5">
                <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Sector de actividad</label>
                <select
                    className="gf-input text-sm"
                    value={extra.sector}
                    onChange={(e) => onChange("sector", e.target.value)}
                    data-testid="extra-sector"
                >
                    {SECTORS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
            </div>

            {Object.entries(groups).map(([gk, g]) => {
                let coherenceMsg = null;
                if (gk === "operativo" && (Number(extra.aprovisionamientos) || Number(extra.gastos_personal) || Number(extra.amortizaciones) || Number(extra.gastos_financieros))) {
                    coherenceMsg = gastosOk
                        ? { ok: true, txt: `Suma desglose ${formatEUR(gastosOperativos, 0)} ≈ Gastos totales ${formatEUR(bal.gastos, 0)}` }
                        : { ok: false, txt: `Suma desglose ${formatEUR(gastosOperativos, 0)} vs Gastos totales ${formatEUR(bal.gastos, 0)} (dif ${formatEUR(gastosDiff, 0)})` };
                }
                if (gk === "tesoreria" && (efectivo || deudores)) {
                    coherenceMsg = acOk
                        ? { ok: true, txt: `Efectivo + Deudores + Existencias = ${formatEUR(acDesglose, 0)} ≈ Activo corriente ${formatEUR(bal.ac, 0)}` }
                        : { ok: false, txt: `Efectivo + Deudores + Existencias = ${formatEUR(acDesglose, 0)} vs Activo corriente ${formatEUR(bal.ac, 0)} (dif ${formatEUR(acDiff, 0)})` };
                }
                if (gk === "estructura" && (reservas || rdoAnt)) {
                    coherenceMsg = pnOk
                        ? { ok: true, txt: `Reservas + Rdo. anterior + Rdo. ejercicio ≈ PN (${formatEUR(bal.pn, 0)})` }
                        : { ok: false, txt: `Suma componentes ${formatEUR(pnDesglose, 0)} vs Patrimonio Neto ${formatEUR(bal.pn, 0)}` };
                }
                return (
                    <div key={gk} className="mb-5">
                        <div className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)] mb-2">{g.label}</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {g.fields.map((f) => (
                                <div key={f.k}>
                                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block" title={f.hint}>
                                        {f.label}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="gf-input mono pr-8 text-sm"
                                            value={extra[f.k]}
                                            onChange={(e) => onChange(f.k, e.target.value)}
                                            placeholder="0"
                                            data-testid={`extra-${f.k}`}
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--gf-text-muted)] pointer-events-none">€</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {coherenceMsg && (
                            <div className={`mt-2 text-[11px] border-l-2 pl-2 py-1 ${coherenceMsg.ok ? "border-l-emerald-500 bg-emerald-50 text-emerald-800" : "border-l-amber-500 bg-amber-50 text-amber-800"}`} data-testid={`coherence-${gk}`}>
                                {coherenceMsg.ok ? "✓" : "⚠"} {coherenceMsg.txt}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function RefRow({ label, value, highlight, sub }) {
    return (
        <div className={`flex items-baseline justify-between gap-2 ${sub ? "pl-3 border-l border-[var(--gf-border)]" : ""}`}>
            <span className={`text-[10px] uppercase tracking-wider ${sub ? "text-[var(--gf-text-muted)]" : "text-[var(--gf-text-secondary)]"}`}>{label}</span>
            <span className={`mono ${highlight ? "font-bold text-[var(--gf-navy)]" : "font-semibold text-[var(--gf-text)]"}`}>{formatEUR(value, 0)}</span>
        </div>
    );
}

/* ============================================================ */
/*  RatioCompare: muestra antes vs después con color             */
/* ============================================================ */
function RatioCompare({ before, after }) {
    const colors = {
        verde: "var(--gf-success)",
        ambar: "var(--gf-warning)",
        rojo: "var(--gf-danger)",
        neutral: "var(--gf-text-muted)",
        "n/a": "var(--gf-text-muted)",
    };
    function fmt(r) {
        if (r.value === null || r.value === undefined || Number.isNaN(r.value)) return "—";
        if (r.kind === "pct") return `${formatNum(r.value * 100, 1)}%`;
        return formatNum(r.value, 2);
    }
    const improved = before.level !== after.level && (
        (before.level === "rojo" && (after.level === "ambar" || after.level === "verde")) ||
        (before.level === "ambar" && after.level === "verde")
    );
    return (
        <div className="border border-[var(--gf-border)] p-3">
            <div className="flex items-center justify-between mb-1">
                <span className="micro-label text-[10px]">{after.label}</span>
                <span className="text-[9px] mono text-[var(--gf-text-muted)]">óptimo {after.optimal}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 items-center">
                <div>
                    <div className="text-[9px] tracking-widest uppercase text-[var(--gf-text-muted)] mb-0.5">Antes</div>
                    <div className="mono text-sm font-semibold" style={{ color: colors[before.level] || colors["n/a"] }}>
                        {fmt(before)}
                    </div>
                    <Badge level={before.level} />
                </div>
                <div className="border-l border-[var(--gf-border)] pl-2">
                    <div className="text-[9px] tracking-widest uppercase text-[var(--gf-gold)] mb-0.5 flex items-center gap-1">
                        Después {improved && <Sparkle size={9} weight="fill" />}
                    </div>
                    <div className="mono text-sm font-semibold" style={{ color: colors[after.level] || colors["n/a"] }}>
                        {fmt(after)}
                    </div>
                    <Badge level={after.level} />
                </div>
            </div>
            {after.requires && (
                <div className="mt-2 text-[10px] text-[var(--gf-text-muted)] italic border-t border-dashed border-[var(--gf-border)] pt-1.5">
                    💡 {after.requires}
                </div>
            )}
        </div>
    );
}

function Badge({ level }) {
    const map = {
        verde: { c: "var(--gf-success)", t: "Saludable" },
        ambar: { c: "var(--gf-warning)", t: "Atención" },
        rojo: { c: "var(--gf-danger)", t: "Riesgo" },
        neutral: { c: "var(--gf-text-muted)", t: "Falta dato" },
        "n/a": { c: "var(--gf-text-muted)", t: "—" },
    };
    const m = map[level] || map["n/a"];
    return (
        <span className="inline-block text-[9px] tracking-widest uppercase mt-0.5 font-semibold" style={{ color: m.c }}>
            {m.t}
        </span>
    );
}

/* ============================================================ */
/*  ResultView: dictamen final                                   */
/* ============================================================ */
function ResultView({ result, onReset, analysisId }) {
    const veredicto = result.veredicto || "apto";
    const veredictoColors = {
        no_apto: "bg-red-50 border-l-[var(--gf-danger)] text-[var(--gf-danger)]",
        apto_condicionado: "bg-amber-50 border-l-[var(--gf-warning)] text-[var(--gf-warning)]",
        apto: "bg-green-50/60 border-l-[var(--gf-success)] text-[var(--gf-success)]",
    };
    const veredictoIcons = {
        no_apto: <XCircle size={28} weight="fill" />,
        apto_condicionado: <Warning size={28} weight="fill" />,
        apto: <CheckCircle size={28} weight="fill" />,
    };
    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="audit-result">
            {/* Veredicto */}
            <div className={`gf-card border-l-4 p-6 mb-6 ${veredictoColors[veredicto] || veredictoColors.apto}`}>
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">{veredictoIcons[veredicto]}</div>
                    <div className="flex-1">
                        <div className="micro-label mb-1 text-[var(--gf-text-muted)]">— Dictamen</div>
                        <h2 className="serif text-2xl md:text-3xl mb-2 text-[var(--gf-navy)]">{result.veredicto_texto}</h2>
                        <p className="text-sm text-[var(--gf-text-secondary)]">{result.metodologia}</p>
                    </div>
                </div>
            </div>

            {/* Ratios antes/después */}
            <div className="gf-card mb-6">
                <div className="micro-label mb-2">— Comparativa de ratios</div>
                <h3 className="serif text-2xl mb-4">Antes vs Después</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(result.ratios_bancarios).map(([k, r]) => (
                        <RatioBlock key={k} label={r.label} optimal={r.optimal} after={r} before={result.ratios_antes?.[k]} />
                    ))}
                </div>
            </div>

            {/* Balance rectificado vs original */}
            {(result.balance_original || result.balance_corregido) && (
                <BalanceCompare
                    original={result.balance_original || {}}
                    corrected={result.balance_corregido || {}}
                />
            )}

            {/* Findings */}
            {result.findings && result.findings.length > 0 && (
                <div className="gf-card mb-6">
                    <div className="micro-label mb-2">— Puntos detectados</div>
                    <h3 className="serif text-2xl mb-4">Lo que el banco verá</h3>
                    <div className="space-y-3">
                        {result.findings.map((f, i) => (
                            <FindingBlock key={i} f={f} />
                        ))}
                    </div>
                </div>
            )}

            {/* Suggestions */}
            {result.suggestions && result.suggestions.length > 0 && (
                <div className="gf-card mb-6 border-t-4 border-t-[var(--gf-gold)]">
                    <div className="micro-label mb-2">— Plan de acción</div>
                    <h3 className="serif text-2xl mb-4">Correcciones recomendadas</h3>
                    <div className="space-y-4">
                        {result.suggestions.map((s, i) => (
                            <SuggestionBlock key={i} s={s} idx={i + 1} />
                        ))}
                    </div>
                </div>
            )}

            {/* Footer actions */}
            <div className="flex flex-wrap gap-3 justify-between items-center pt-6 border-t border-[var(--gf-border)]">
                <button onClick={onReset} className="btn-outline" data-testid="audit-redo">
                    <ArrowLeft size={16} /> Editar y volver a generar
                </button>
                <Link to={`/analisis/${analysisId}`} className="btn-gold" data-testid="audit-back-report">
                    Volver al informe completo <ArrowRight size={16} />
                </Link>
            </div>
        </motion.div>
    );
}

function RatioBlock({ label, optimal, after, before }) {
    const colors = {
        verde: "var(--gf-success)",
        ambar: "var(--gf-warning)",
        rojo: "var(--gf-danger)",
        "n/a": "var(--gf-text-muted)",
    };
    function fmt(r) {
        if (!r || r.value === null || r.value === undefined || Number.isNaN(r.value)) return "—";
        const pctFields = ["autonomía", "autonomia"];
        const isPct = (r.label || "").toLowerCase().includes("autonom") || (r.label || "").toLowerCase().includes("%") || (r.optimal || "").includes("%");
        return isPct ? `${formatNum(r.value * 100, 1)}%` : formatNum(r.value, 2);
    }
    return (
        <div className="border border-[var(--gf-border)] p-3">
            <div className="flex items-center justify-between mb-2">
                <span className="micro-label text-[10px]">{label}</span>
                <span className="text-[9px] mono text-[var(--gf-text-muted)]">{optimal}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <div className="text-[9px] tracking-widest uppercase text-[var(--gf-text-muted)]">Antes</div>
                    <div className="mono font-semibold text-base" style={{ color: colors[before?.level || "n/a"] }}>{fmt(before)}</div>
                </div>
                <div className="border-l border-[var(--gf-border)] pl-2">
                    <div className="text-[9px] tracking-widest uppercase text-[var(--gf-gold)]">Después</div>
                    <div className="mono font-semibold text-base" style={{ color: colors[after.level] }}>{fmt(after)}</div>
                </div>
            </div>
        </div>
    );
}

function FindingBlock({ f }) {
    const severityColors = {
        rojo: "border-l-[var(--gf-danger)] bg-red-50",
        ambar: "border-l-[var(--gf-warning)] bg-amber-50",
        verde: "border-l-[var(--gf-success)] bg-green-50/60",
    };
    const severityIcons = {
        rojo: <XCircle size={18} weight="fill" className="text-[var(--gf-danger)]" />,
        ambar: <Warning size={18} weight="fill" className="text-[var(--gf-warning)]" />,
        verde: <CheckCircle size={18} weight="fill" className="text-[var(--gf-success)]" />,
    };
    return (
        <div className={`border-l-4 p-4 ${severityColors[f.severity] || severityColors.ambar}`}>
            <div className="flex items-start gap-3">
                <div className="mt-0.5">{severityIcons[f.severity]}</div>
                <div className="flex-1">
                    <div className="font-semibold text-sm text-[var(--gf-navy)] mb-1">{f.titulo}</div>
                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{f.detalle}</p>
                    {f.impacto && (
                        <p className="text-xs text-[var(--gf-text-muted)] mt-2 italic">Impacto: {f.impacto}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

function SuggestionBlock({ s, idx }) {
    return (
        <div className="border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-cream)] p-4">
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[var(--gf-gold)] text-white flex items-center justify-center font-semibold text-sm mono">
                    {idx}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <Lightbulb size={16} weight="fill" className="text-[var(--gf-gold)]" />
                        <div className="font-semibold text-sm text-[var(--gf-navy)]">{s.accion}</div>
                    </div>
                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-2">{s.como}</p>
                    {s.impacto_ratios && (
                        <div className="text-xs text-[var(--gf-success)] mt-2 border-l-2 border-[var(--gf-success)] pl-3 py-1 bg-green-50/60">
                            <strong>Impacto en ratios:</strong> {s.impacto_ratios}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ============================================================ */
/*  BalanceCompare: original vs rectificado                      */
/* ============================================================ */
function BalanceCompare({ original, corrected }) {
    const rows = [
        { k: "activos_no_corrientes", label: "Activo no corriente", group: "activo" },
        { k: "activos_corrientes", label: "Activo corriente", group: "activo" },
        { k: "patrimonio_neto", label: "Fondos propios (Patrimonio neto)", group: "pat" },
        { k: "pasivos_no_corrientes", label: "Pasivo no corriente", group: "pasivo" },
        { k: "pasivos_corrientes", label: "Pasivo corriente", group: "pasivo" },
        { k: "ingresos", label: "Ingresos", group: "pyg" },
        { k: "gastos", label: "Gastos", group: "pyg" },
    ];

    const num = (o, k) => Number(o?.[k]) || 0;
    const activoO = num(original, "activos_no_corrientes") + num(original, "activos_corrientes");
    const activoC = num(corrected, "activos_no_corrientes") + num(corrected, "activos_corrientes");
    const ppO = num(original, "patrimonio_neto") + num(original, "pasivos_no_corrientes") + num(original, "pasivos_corrientes");
    const ppC = num(corrected, "patrimonio_neto") + num(corrected, "pasivos_no_corrientes") + num(corrected, "pasivos_corrientes");
    const resO = num(original, "ingresos") - num(original, "gastos");
    const resC = num(corrected, "ingresos") - num(corrected, "gastos");

    return (
        <div className="gf-card mb-6" data-testid="balance-compare">
            <div className="micro-label mb-2">— Balance rectificado</div>
            <h3 className="serif text-2xl mb-4">Cifras antes vs después de la corrección</h3>
            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="balance-compare-table">
                    <thead>
                        <tr className="border-b border-[var(--gf-border)] text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)]">
                            <th className="text-left py-2 pr-4 font-medium">Partida</th>
                            <th className="text-right py-2 px-3 font-medium">Antes</th>
                            <th className="text-right py-2 px-3 font-medium">Después</th>
                            <th className="text-right py-2 pl-3 font-medium">Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const a = num(original, r.k);
                            const b = num(corrected, r.k);
                            const d = b - a;
                            const changed = Math.abs(d) > 0.01;
                            return (
                                <tr key={r.k} className={`border-b border-[var(--gf-border)]/50 ${changed ? "bg-[var(--gf-gold)]/5" : ""}`} data-testid={`compare-row-${r.k}`}>
                                    <td className="py-2 pr-4 text-[var(--gf-text-secondary)]">{r.label}</td>
                                    <td className="py-2 px-3 text-right mono">{formatEUR(a)}</td>
                                    <td className={`py-2 px-3 text-right mono ${changed ? "font-semibold text-[var(--gf-navy)]" : ""}`}>{formatEUR(b)}</td>
                                    <td className={`py-2 pl-3 text-right mono text-xs ${d > 0 ? "text-emerald-600" : d < 0 ? "text-red-600" : "text-[var(--gf-text-muted)]"}`}>
                                        {changed ? `${d > 0 ? "+" : ""}${formatEUR(d)}` : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="border-t-2 border-[var(--gf-navy)] font-semibold">
                            <td className="py-2 pr-4">Total Activo</td>
                            <td className="py-2 px-3 text-right mono">{formatEUR(activoO)}</td>
                            <td className="py-2 px-3 text-right mono">{formatEUR(activoC)}</td>
                            <td className="py-2 pl-3 text-right mono text-xs">{Math.abs(activoC - activoO) > 0.01 ? `${activoC - activoO > 0 ? "+" : ""}${formatEUR(activoC - activoO)}` : "—"}</td>
                        </tr>
                        <tr className="font-semibold">
                            <td className="py-2 pr-4">Total Pasivo + Patrimonio</td>
                            <td className="py-2 px-3 text-right mono">{formatEUR(ppO)}</td>
                            <td className="py-2 px-3 text-right mono">{formatEUR(ppC)}</td>
                            <td className="py-2 pl-3 text-right mono text-xs">{Math.abs(ppC - ppO) > 0.01 ? `${ppC - ppO > 0 ? "+" : ""}${formatEUR(ppC - ppO)}` : "—"}</td>
                        </tr>
                        <tr className="font-semibold text-[var(--gf-gold)]">
                            <td className="py-2 pr-4">Resultado del ejercicio</td>
                            <td className="py-2 px-3 text-right mono">{formatEUR(resO)}</td>
                            <td className="py-2 px-3 text-right mono">{formatEUR(resC)}</td>
                            <td className="py-2 pl-3 text-right mono text-xs">{Math.abs(resC - resO) > 0.01 ? `${resC - resO > 0 ? "+" : ""}${formatEUR(resC - resO)}` : "—"}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <p className="text-xs text-[var(--gf-text-muted)] mt-3">
                Las filas resaltadas en dorado son las partidas que has modificado. Los ratios de arriba se calculan sobre la columna «Después».
            </p>
        </div>
    );
}
